// [목적] 템플릿 기반 폴리오 생성 mutation hook — POST /folios(createFolio) 호출 후 편집 화면으로 이동
// [이유] 기존 생성 경로는 RAG(/chat) 뿐이라 이미 존재하던 folio-crud POST /folios 를 소비하는 FE 가 없었다.
//         FOLIO_TEMPLATES 프리셋을 그대로 보내 days/items 까지 한 번에 만들고 /folio/:id/edit 로 보낸다.
// [사이드 이팩트] 성공 시 ['folio-list'] 캐시 invalidate(내 폴리오 목록 즉시 갱신) + navigate 로 화면 전환.
//                 실패 시 토스트만 띄우고 화면 유지 — 부분 생성은 BE 가 트랜잭션으로 막는다(FoliosService.create withTx).
// [수정 시 고려사항] 인증 확인은 호출처 책임 — /me/folios 는 ProtectedRoute(requireOnboarded) 하위라 별도 가드 없음.
//                   중복 클릭 방지는 호출부에서 isPending 으로 버튼 disabled 처리할 것.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Folio } from '@gangwoncanvas/types';

import { createFolio } from '../api/folioClient.js';
import { useToast } from '../components/feedback/Toast.js';
import type { FolioTemplate } from '../constants/folioTemplates.js';

/**
 * [목적] 템플릿 카드 클릭 → 폴리오 생성 → 편집 화면 이동까지의 흐름을 하나로 묶는다
 * [이유] 데스크톱/모바일 양쪽 레이아웃에서 동일 로직을 재사용하기 위함
 * [사이드 이팩트] navigate(`/folio/:id/edit`), 토스트, folio-list invalidate
 * [수정 시 고려사항] 생성 직후 편집이 아니라 뷰로 보내고 싶으면 이동 경로만 교체하면 된다(호출부 변경 불필요)
 */
export function useCreateFolioFromTemplate() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { show } = useToast();

  return useMutation<Folio, Error, FolioTemplate>({
    mutationFn: (template) => createFolio(template.build()),
    onSuccess: (folio, template) => {
      void qc.invalidateQueries({ queryKey: ['folio-list'] });
      show(`'${template.label}' 템플릿으로 폴리오를 만들었어요.`, 'success');
      navigate(`/folio/${folio.id}/edit`);
    },
    onError: () => {
      show('폴리오를 만들지 못했어요. 잠시 후 다시 시도해 주세요.', 'error');
    },
  });
}
